import api from './api';

// ── Response Normalizers ────────────────────────────────────────────────
// Temporary mappers that transform raw backend responses into the
// StandardResponse shape { status, message, data } that components expect.
// Remove these once the backend wraps responses in StandardResponse.

function normalizeOverview(raw: Record<string, any>) {
  const asisSummary = raw.asis_summary || [];
  const healthCard = asisSummary.find((s: any) => s.category?.toLowerCase().includes('health')) || asisSummary[0] || {};
  return {
    ...raw,
    profile_health_score: healthCard.score_pct || 0,
    keywords_identified: raw.local_keywords?.count || 0,
  };
}

function normalizeAsIs(raw: Record<string, any>) {
  const profile_breakdown = (raw.health_breakdown || []).map((h: any) => ({
    label: h.category,
    value: h.score_pct,
  }));

  const KPI_LABELS: Record<string, string> = {
    profile_views: 'Impressions',
  };

  const kpis = Object.entries(raw.gmb_kpis || {})
    .filter(([, v]: [string, any]) => v != null)
    .map(([key, v]: [string, any]) => ({
      label: KPI_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
      value: String(v.value ?? '—'),
      previous: v.prev != null ? String(v.prev) : null,
      change: v.change_pct != null ? `${v.change_pct > 0 ? '+' : ''}${v.change_pct}%` : '--',
      trend: (v.trend || 'flat') as 'up' | 'down' | 'flat',
    }));

  const brand = (raw.competition_comparison || []).find((c: any) => c.is_brand) || {};
  const competitors = (raw.competition_comparison || []).filter((c: any) => !c.is_brand);

  const metrics = [
    { key: 'profile_health_score', label: 'Health Score' },
    { key: 'avg_rating', label: 'Avg Rating' },
    { key: 'total_reviews', label: 'Total Reviews' },
    { key: 'photo_count', label: 'Photos' },
    { key: 'posts_last_30d', label: 'Posts (30d)' },
  ];

  const compKeys = competitors.map((_: any, i: number) => `comp${String.fromCharCode(65 + i)}`);

  const rows = metrics.map(({ key, label }) => {
    const row: any = {
      metric: label,
      yours: {
        value: String((brand as any)[key] ?? '—'),
        pill: brand[`${key}_pill`] || undefined,
      },
    };
    competitors.forEach((c: any, i: number) => {
      row[compKeys[i]] = {
        value: String(c[key] ?? '—'),
        pill: c[`${key}_pill`] || undefined,
      };
    });

    // Post-process competitor columns for API limitations
    if (key === 'photo_count') {
      compKeys.forEach((comp: string) => {
        if (row[comp]?.value === '10') row[comp].value = '10+';
      });
    }
    if (key === 'posts_last_30d') {
      compKeys.forEach((comp: string) => {
        if (row[comp]?.value === '0' || row[comp]?.value === '—') {
          row[comp].value = 'N/A';
          row[comp].pill = undefined;
        }
      });
    }

    return row;
  });

  // Descriptive rows (BRD competition output): no better/worse pills.
  const formatReviewDate = (iso: string | null | undefined) => {
    if (!iso) return '—';
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
      ? '—'
      : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const textRows: Array<{ key: string; label: string; format: (v: any) => string }> = [
    { key: 'category', label: 'Category', format: (v) => v || '—' },
    { key: 'last_review_at', label: 'Latest review', format: formatReviewDate },
    { key: 'activity_level', label: 'Posting activity', format: (v) => v || 'N/A' },
  ];
  textRows.forEach(({ key, label, format }) => {
    const row: any = { metric: label, yours: { value: format((brand as any)[key]) } };
    competitors.forEach((c: any, i: number) => {
      row[compKeys[i]] = { value: format(c[key]) };
    });
    rows.push(row);
  });

  return {
    keyword_position: raw.keyword_position ?? null,
    calculated_at: raw.calculated_at,
    profile_health_score: raw.profile_health_score,
    total_reviews: raw.total_reviews,
    profile_breakdown,
    kpis,
    kpi_periods: raw.kpi_periods,
    competition: {
      headers: ['Metric', 'Yours', ...competitors.map((c: any) => c.label)],
      compKeys,
      rows,
      competitors: competitors.map((c: any) => ({
        label: c.label,
        competitor_url: c.competitor_url || null,
        competitor_insights: c.competitor_insights || null,
        source: c.source || 'serp_scraped',
      })),
    },
    // Restored 2026-09-30 (audit after DEC-GMB-078) — dropped by the same
    // "Bug fixed D-209 to D-214" commit that broke DEC-GMB-077; this field
    // was added by DEC-GMB-063.
    products_services: raw.products_services || [],
  };
}

function normalizeGoals(raw: Record<string, any>) {
  const goals = (raw.goals || []).map((g: any) => {
    const targetStr = g.target || '';
    const numStr = String(g.target_value ?? '');
    const unit = targetStr.replace(numStr, '').trim();
    return {
      goal_id: g.id,
      kpi_type: g.kpi_type || '',
      goal_type: g.goal_type || g.kpi,
      kpi: g.kpi || '',
      target: g.target || String(g.target_value ?? 0),
      target_value: g.target_value ?? 0,
      current_value: g.baseline_value ?? 0,
      unit,
      status: g.status === 'on_track' ? 'active' : g.status === 'at_risk' ? 'paused' : g.status || 'paused',
    };
  });
  const completed = goals.filter((g: any) => g.status === 'active').length;
  return {
    goals,
    total_goals: goals.length,
    goals_completed: completed,
    completion_rate: goals.length > 0 ? Math.round((completed / goals.length) * 100) : 0,
  };
}

function normalizeTechPlan(raw: Record<string, any>) {
  const mapTask = (t: any) => ({
    task_id: t.id,
    task_name: t.title,
    category: t.category || '',
    status: t.status,
    priority: t.priority || 'medium',
    // Restored 2026-09-30 (audit after DEC-GMB-078) — dropped by the same
    // "Bug fixed D-209 to D-214" commit that broke DEC-GMB-077; this field
    // was added by DEC-GMB-057.
    description: t.description || '',
    // D-205: weekly / monthly cadence and business-specific execution detail
    frequency: t.frequency || null,
    details: t.details || null,
  });

  const setup = raw.one_time_setup || {};
  const maintenance = raw.regular_maintenance || {};

  const checklist = (setup.tasks || []).map(mapTask);
  const maint = (maintenance.tasks || []).map(mapTask);

  return {
    checklist,
    maintenance: maint,
    total_tasks: checklist.length + maint.length,
    pending: [...checklist, ...maint].filter((t: any) => t.status === 'pending').length,
    setup_completed: setup.progress?.done || 0,
    maintenance_completed: maintenance.progress?.done || 0,
    total_setup_tasks: setup.progress?.total || checklist.length,
    total_maintenance_tasks: maintenance.progress?.total || maint.length,
  };
}

function normalizeContentPlan(raw: Record<string, any>) {
  const calendar = (raw.posts || []).map((p: any) => ({
    id: p.id,
    week: p.week_number,
    date: p.scheduled_date ? formatDate(p.scheduled_date) : 'Not scheduled',
    content_intent: p.content_intent || '',
    post_type: formatPostType(p.google_post_type || 'whats_new'),
    title: p.title || p.content_intent || '',
    cta: formatCta(p.cta_type || ''),
    keyword_used: p.keyword_used || '',
    status: p.status || 'draft',
  }));
  return { calendar };
}

function formatPostType(type: string): string {
  const map: Record<string, string> = {
    whats_new: "What's New",
    offer: 'Offer',
    event: 'Event',
  };
  return map[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCta(cta: string): string {
  return cta.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function wrap<T>(data: T, message = '') {
  return { status: 'success', message, data };
}

// ── Implementation Normalizers ───────────────────────────────────────

function normalizeImplementationOverview(raw: Record<string, any>) {
  const oneTime = raw.one_time_tasks || {};
  const weekly = raw.weekly_tasks || {};
  const photos = raw.photos_uploaded || {};
  const videos = raw.videos_uploaded || {};
  const posts = raw.posts_scheduled || {};
  const photoSnap = raw.photo_upload_snapshot || {};
  const postSnap = raw.post_scheduler_snapshot || {};

  const stats = [
    {
      label: 'One-Time Tasks',
      value: `${oneTime.done || 0}/${oneTime.total || 0}`,
      change: `${oneTime.remaining || 0} remaining`,
      color: 'blue',
      icon: 'ri-checkbox-circle-line',
    },
    {
      label: 'Weekly Tasks',
      value: `${weekly.done || 0}/${weekly.total || 0}`,
      change: `${weekly.remaining || 0} remaining`,
      color: 'green',
      icon: 'ri-calendar-check-line',
    },
    {
      label: 'Photos Uploaded',
      value: String(photos.count || 0),
      // Videos live in the same Photo Upload gallery; show them here too so a
      // video-only upload doesn't read as "nothing uploaded".
      change: videos.count
        ? `${videos.count} video${videos.count === 1 ? '' : 's'} · ${photos.still_needed || 0} more needed`
        : `${photos.still_needed || 0} more needed`,
      color: 'purple',
      icon: 'ri-image-line',
    },
    {
      label: 'Posts Scheduled',
      value: String(posts.count || 0),
      change: `${posts.this_week || 0} this week`,
      color: 'orange',
      icon: 'ri-article-line',
    },
  ];

  const snapshots = [
    {
      id: 'photo-upload',
      title: 'Photo Upload',
      borderColor: 'blue',
      gridItems: Object.entries(photoSnap).map(([key, val]) => ({
        label: key.charAt(0).toUpperCase() + key.slice(1),
        status: val ? 'done' : 'needs-more',
      })),
      label: `${photos.still_needed || 0} photos still needed`,
    },
    {
      id: 'post-scheduler',
      title: 'Post Scheduler',
      borderColor: 'green',
      weekDays: postSnap.weekDays || [
        { day: 'Mon', status: 'none' },
        { day: 'Tue', status: 'none' },
        { day: 'Wed', status: 'none' },
        { day: 'Thu', status: 'none' },
        { day: 'Fri', status: 'none' },
        { day: 'Sat', status: 'none' },
        { day: 'Sun', status: 'none' },
      ],
      label: postSnap.next_due_label || 'All posts up to date',
    },
    {
      // Q&A is permanently unavailable — Google discontinued the My
      // Business Q&A API on 2025-11-03 (DEC-GMB-035). Show a clear
      // "coming soon" state instead of a misleading 0/0 (UAT #23).
      id: 'qa-checklist',
      title: 'Q&A & Checklist',
      borderColor: 'purple',
      label: 'Coming soon — Google discontinued the Q&A API',
    },
  ];

  return { stats, snapshots };
}

function normalizePhotos(raw: Record<string, any>) {
  return {
    uploaded_count: raw.uploaded_count || 0,
    remaining_count: raw.remaining_count || 0,
    videos_count: raw.videos_count || 0,
    photos: (raw.photos || []).map((p: any) => ({
      id: p.id,
      photo_type: p.photo_type,
      s3_url: p.s3_url || '',
      file_name: p.file_name || '',
      uploaded_at: p.uploaded_at || '',
    })),
  };
}

function normalizeVideos(raw: Record<string, any>) {
  return {
    videos_uploaded: raw.videos_uploaded || 0,
    total_duration_seconds: raw.total_duration_seconds || 0,
    still_needed: raw.still_needed || 0,
    videos: (raw.videos || []).map((v: any) => ({
      id: v.id,
      s3_url: v.s3_url || '',
      thumbnail_url: v.thumbnail_url || '',
      file_name: v.file_name || '',
      file_size_mb: v.file_size_mb ?? null,
      duration_seconds: v.duration_seconds ?? null,
      video_resolution: v.video_resolution || '',
      uploaded_at: v.uploaded_at || '',
      is_published_to_gmb: v.is_published_to_gmb ?? false,
    })),
  };
}

function normalizePosts(raw: Record<string, any>) {
  return {
    published_count: raw.published_count || 0,
    drafts_count: raw.drafts_count || 0,
    total_count: raw.total_count || 0,
    posts: (raw.posts || []).map((p: any) => ({
      id: p.id,
      post_date: p.post_date || '',
      week_number: p.week_number || 0,
      post_number: p.post_number || 0,
      google_post_type: p.google_post_type || 'whats_new',
      content_intent: p.content_intent || '',
      title: p.title || '',
      caption: p.caption || '',
      cta_type: p.cta_type || '',
      cta_url: p.cta_url || '',
      keyword_used: p.keyword_used || '',
      suggested_image_dir: p.suggested_image_dir || '',
      image_url: p.image_url || '',
      image_description: p.image_description || '',
      status: p.status || 'draft',
      scheduled_date: p.scheduled_date || '',
      is_ai_generated: p.is_ai_generated ?? true,
    })),
  };
}

function normalizeReviews(raw: Record<string, any>) {
  return {
    reviews: (raw.reviews || []).map((r: any) => ({
      id: r.id,
      reviewer_name: r.reviewer_name || 'Anonymous',
      rating: r.rating || 0,
      review_text: r.review_text || '',
      review_date: r.review_date || '',
      response_status: r.response_status || 'pending',
      response_text: r.response_text || '',
      ai_suggested_response: r.ai_suggested_response || '',
      sentiment: r.sentiment || 'neutral',
    })),
    total: raw.total || 0,
  };
}

function normalizeReviewOverview(raw: Record<string, any>) {
  const trend = raw.rating_trend || {};
  return {
    total_reviews: raw.total_reviews || 0,
    avg_rating: raw.avg_rating || 0,
    this_month: raw.this_month || 0,
    responded_count: raw.responded_count || 0,
    not_responded_count: raw.not_responded_count || 0,
    response_rate_pct: raw.response_rate_pct || 0,
    rating_trend: trend.direction || 'flat',
    rating_change: trend.change || 0,
    rating_trend_label: trend.label || '',
    rating_comparison_available: trend.comparison_available === true,
  };
}

function normalizeQa(raw: Record<string, any>) {
  return {
    total: raw.total || 0,
    answered: raw.answered || 0,
    unanswered: raw.unanswered || 0,
    questions: (raw.questions || []).map((q: any) => ({
      id: q.id,
      question_text: q.question_text || '',
      question_date: q.question_date || '',
      asker_name: q.asker_name || 'Anonymous',
      answer_text: q.answer_text || '',
      is_answered: q.is_answered ?? false,
      is_flagged: q.is_flagged ?? false,
    })),
  };
}

// ── Governance Normalizers ───────────────────────────────────────────

function normalizeGovernanceOverview(raw: Record<string, any>) {
  return {
    live_profile_status: raw.live_profile_status || 'inactive',
    active_alerts_count: raw.active_alerts_count ?? 0,
    google_account_status: raw.google_account_status || 'unlinked',
    verification_status: raw.verification_status || 'unverified',
    live_preview_snapshot: raw.live_preview_snapshot || {},
    activity_log_summary: (raw.activity_log_summary || []).map((entry: any) => ({
      action: entry.action || '',
      user: entry.user || '',
      time: entry.time || '',
    })),
    changes_today: raw.changes_today ?? 0,
    alerts_summary: (raw.alerts_summary || []).map((a: any) => ({
      type: a.type || '',
      severity: a.severity || '',
      title: a.title || '',
    })),
    active_alerts_count_by_severity: raw.active_alerts_count_by_severity || {
      critical: 0,
      warning: 0,
      informational: 0,
    },
  };
}

// Absolute "Oct 14, 2026, 10:32 AM" in the viewer's timezone. The backend
// stores UTC without an offset, so an offset-less ISO string is read as UTC.
function formatActivityDate(timestamp: string | null | undefined): string {
  if (!timestamp) return '';
  const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(timestamp) ? timestamp : `${timestamp}Z`;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function normalizeActivityLog(raw: Record<string, any>) {
  return {
    total: raw.total ?? 0,
    completed: raw.completed ?? 0,
    in_progress: raw.in_progress ?? 0,
    failed: raw.failed ?? 0,
    page: raw.page ?? 1,
    page_size: raw.page_size ?? 25,
    activities: (raw.activities || []).map((a: any) => ({
      id: a.id,
      action_type: a.action_type || '',
      entity_type: a.entity_type || 'General',
      description: a.description || '',
      performed_by: a.performed_by || '',
      timestamp: a.timestamp || null,
      timestamp_label: a.timestamp_label || '',
      date_label: formatActivityDate(a.timestamp) || a.date_label || '',
      status: a.status || 'success',
    })),
  };
}

function normalizeAlerts(raw: Record<string, any>) {
  return {
    total: raw.total ?? 0,
    critical_count: raw.critical_count ?? 0,
    warning_count: raw.warning_count ?? 0,
    informational_count: raw.informational_count ?? 0,
    alerts: (raw.alerts || []).map((a: any) => ({
      id: a.id,
      severity: a.severity || 'informational',
      alert_type: a.alert_type || '',
      alert_type_label: a.alert_type_label || '',
      title: a.title || '',
      message: a.message || '',
      cta_label: a.cta_label || '',
      cta_action: a.cta_action || '',
      is_acknowledged: a.is_acknowledged ?? false,
      created_at: a.created_at || null,
      created_at_label: a.created_at_label || '',
    })),
  };
}

// ── API Service ─────────────────────────────────────────────────────────

class GmbApiService {
  // ── Phase 0: Foundation ──────────────────────────────────────────────

  async getVisibility(userId: string) {
    return api.get<any>(`/gmb/visibility?user_id=${userId}`);
  }

  async initiateOAuth(userId: string) {
    return api.get<any>(`/gmb/oauth/initiate?user_id=${userId}`);
  }

  async revokeOAuth(userId: string) {
    return api.request<any>(`/gmb/oauth/revoke?user_id=${userId}`, { method: 'DELETE' });
  }

  // ── Phase 1: Onboarding & Sync ───────────────────────────────────────

  async getConnectionStatus(userId: string) {
    return api.get<any>(`/gmb/connection-status?user_id=${userId}`);
  }

  // D-198: Business Profiles under the connected Google login, and choosing one.
  async listLocations(userId: string) {
    return api.get<any>(`/gmb/locations?user_id=${userId}`);
  }

  async selectLocation(userId: string, locationName: string) {
    return api.post<any>(
      `/gmb/locations/select?user_id=${userId}&location_name=${encodeURIComponent(locationName)}`
    );
  }

  async triggerSync(userId: string) {
    return api.post<any>(`/gmb/sync?user_id=${userId}`);
  }

  async getProfileSetup(userId: string) {
    return api.get<any>(`/gmb/profile-setup?user_id=${userId}`);
  }

  async updateProfileSetup(userId: string, data: {
    gmb_profile_url: string;
    business_name: string;
    business_description: string;
  }) {
    return api.request<any>(`/gmb/profile-setup?user_id=${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Restored 2026-09-30 (DEC-GMB-078) — dropped by a merge that replaced
  // this pair with getProfileSetup/updateProfileSetup above (a different,
  // pre-OAuth flow); the standalone /gmb-onboarding page still depends on
  // these for the post-OAuth mandatory setup screen (D-194/D-195/D-198).
  async getSetupInputsNeeded(userId: string) {
    return api.get<any>(`/gmb/onboarding/setup-inputs-needed?user_id=${userId}`);
  }

  async updateSetupInputs(userId: string, payload: Record<string, unknown> | object) {
    return api.request<any>(`/gmb/onboarding/setup-inputs?user_id=${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  // ── Phase 2: Strategy ────────────────────────────────────────────────

  async getStrategyOverview(userId: string) {
    const raw = await api.get<any>(`/gmb/strategy/overview?user_id=${userId}`);
    return wrap(normalizeOverview(raw), 'Strategy overview retrieved');
  }

  async getAsIs(userId: string) {
    const raw = await api.get<any>(`/gmb/strategy/asis?user_id=${userId}`);
    return wrap(normalizeAsIs(raw), 'As-Is state retrieved');
  }

  async getGoals(userId: string) {
    const raw = await api.get<any>(`/gmb/strategy/goals?user_id=${userId}`);
    return wrap(normalizeGoals(raw), 'Goals retrieved');
  }

  async createGoal(userId: string, goalData: { goal_type: string; target: string }) {
    return api.post<any>(`/gmb/strategy/goals?user_id=${userId}`, goalData);
  }

  async updateGoal(userId: string, goalId: string, data: { target?: string; status?: string }) {
    return api.request<any>(`/gmb/strategy/goals/${goalId}?user_id=${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getTechnicalPlan(userId: string) {
    const raw = await api.get<any>(`/gmb/strategy/action-plan/technical?user_id=${userId}`);
    return wrap(normalizeTechPlan(raw), 'Technical action plan retrieved');
  }

  async getContentPlan(userId: string) {
    const raw = await api.get<any>(`/gmb/strategy/action-plan/content?user_id=${userId}`);
    return wrap(normalizeContentPlan(raw), 'Content plan retrieved');
  }

  async generateContentPlan(userId: string) {
    return api.post<any>(`/gmb/strategy/action-plan/content/generate?user_id=${userId}`);
  }

  async updateContentPostStatus(userId: string, postId: string, status: string) {
    return api.request<any>(`/gmb/strategy/action-plan/content/status?user_id=${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ post_id: postId, status }),
    });
  }

  async getDescriptionGuide(userId: string) {
    return api.get<any>(`/gmb/strategy/action-plan/description-guide?user_id=${userId}`);
  }

  // Business-specific steps for technical action plan tasks (D-205).
  async generateTaskDetails(userId: string, force = false) {
    return api.post<any>(`/gmb/strategy/action-plan/technical/details?user_id=${userId}&force=${force}`);
  }

  // AI-suggested rewrite of the GMB description; nothing is saved.
  async suggestDescription(userId: string) {
    return api.post<any>(`/gmb/strategy/action-plan/description-guide/suggest?user_id=${userId}`);
  }

  async saveDescriptionFromGuide(userId: string, description: string) {
    return api.request<any>(`/gmb/strategy/action-plan/description-guide?user_id=${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ description }),
    });
  }

  async updateTaskStatus(userId: string, taskId: string, status: string) {
    return api.request<any>(`/gmb/strategy/action-plan/tasks/${taskId}?user_id=${userId}&status=${status}`, {
      method: 'PATCH',
    });
  }

  async generateStrategy(userId: string) {
    return api.post<any>(`/gmb/strategy/generate?user_id=${userId}`);
  }

  // ── Phase 3: Implementation ──────────────────────────────────────────

  async getImplementationOverview(userId: string) {
    const raw = await api.get<any>(`/gmb/implementation/overview?user_id=${userId}`);
    return wrap(normalizeImplementationOverview(raw), 'Implementation overview retrieved');
  }

  async getPhotos(userId: string) {
    const raw = await api.get<any>(`/gmb/implementation/photos?user_id=${userId}`);
    return wrap(normalizePhotos(raw), 'Photos retrieved');
  }

  async uploadPhoto(userId: string, formData: FormData) {
    return api.request<any>(`/gmb/implementation/photos?user_id=${userId}`, {
      method: 'POST',
      body: formData,
    });
  }

  async deletePhoto(userId: string, photoId: string) {
    return api.request<any>(`/gmb/implementation/photos/${photoId}?user_id=${userId}`, {
      method: 'DELETE',
    });
  }

  async getVideos(userId: string) {
    const raw = await api.get<any>(`/gmb/implementation/videos?user_id=${userId}`);
    return wrap(normalizeVideos(raw), 'Videos retrieved');
  }

  async uploadVideo(userId: string, formData: FormData) {
    return api.request<any>(`/gmb/implementation/videos?user_id=${userId}`, {
      method: 'POST',
      body: formData,
    });
  }

  async getPosts(userId: string) {
    const raw = await api.get<any>(`/gmb/implementation/posts?user_id=${userId}`);
    return wrap(normalizePosts(raw), 'Posts retrieved');
  }

  async updatePost(userId: string, postId: string, data: any) {
    return api.request<any>(`/gmb/implementation/posts/${postId}?user_id=${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // imageUrl: describe the image currently shown in the edit modal (gallery
  // pick / fresh upload) rather than the post's saved image.
  async generatePostImageDescription(userId: string, postId: string, imageUrl?: string) {
    const imageParam = imageUrl ? `&image_url=${encodeURIComponent(imageUrl)}` : '';
    return api.post<any>(
      `/gmb/implementation/posts/${postId}/image-description?user_id=${userId}${imageParam}`
    );
  }

  async publishPost(userId: string, postId: string) {
    return api.post<any>(`/gmb/implementation/posts/${postId}/publish?user_id=${userId}`);
  }

  async schedulePost(userId: string, postId: string, scheduledDate?: string) {
    const query = scheduledDate ? `&scheduled_date=${encodeURIComponent(scheduledDate)}` : '';
    return api.post<any>(`/gmb/implementation/posts/${postId}/schedule?user_id=${userId}${query}`);
  }

  async editPostImage(userId: string, postId: string, prompt: string, imageUrl?: string) {
    return api.post<any>(`/gmb/implementation/posts/${postId}/edit-image?user_id=${userId}`, {
      prompt,
      image_url: imageUrl || null,
    });
  }

  async downloadPost(userId: string, postId: string) {
    const blob = await api.getBlob<Blob>(`/gmb/implementation/posts/${postId}/download?user_id=${userId}`);
    return { blob };
  }

  async getReviews(userId: string) {
    const raw = await api.get<any>(`/gmb/implementation/reviews?user_id=${userId}`);
    return wrap(normalizeReviews(raw), 'Reviews retrieved');
  }

  async getReviewsOverview(userId: string) {
    const raw = await api.get<any>(`/gmb/implementation/reviews/overview?user_id=${userId}`);
    return wrap(normalizeReviewOverview(raw), 'Review overview retrieved');
  }

  async generateReviewResponse(userId: string, reviewId: string) {
    return api.post<any>(`/gmb/implementation/reviews/${reviewId}/generate-response?user_id=${userId}`);
  }

  async markReviewResponded(userId: string, reviewId: string, responseText: string) {
    return api.request<any>(`/gmb/implementation/reviews/${reviewId}/mark-responded?user_id=${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ response_text: responseText }),
    });
  }

  async getQAItems(userId: string) {
    const raw = await api.get<any>(`/gmb/implementation/qa?user_id=${userId}`);
    return wrap(normalizeQa(raw), 'Q&A items retrieved');
  }

  async submitQAAnswer(userId: string, qaId: string, answerText: string) {
    return api.post<any>(`/gmb/implementation/qa/${qaId}/answer?user_id=${userId}`, { answer_text: answerText });
  }

  async flagQAItem(userId: string, qaId: string) {
    return api.post<any>(`/gmb/implementation/qa/${qaId}/flag?user_id=${userId}`);
  }

  async getChecklist(userId: string) {
    return api.get<any>(`/gmb/implementation/checklist?user_id=${userId}`);
  }

  // ── Phase 4: Governance ──────────────────────────────────────────────

  async getGovernanceOverview(userId: string) {
    const raw = await api.get<any>(`/gmb/governance/overview?user_id=${userId}`);
    return wrap(normalizeGovernanceOverview(raw), 'Governance overview retrieved');
  }

  async getLivePreview(userId: string) {
    const raw = await api.get<any>(`/gmb/governance/live-preview?user_id=${userId}`);
    return wrap(raw, 'Live preview retrieved');
  }

  async getPublishingStatus(userId: string) {
    const raw = await api.get<any>(`/gmb/governance/publishing-status?user_id=${userId}`);
    return wrap(raw, 'Publishing status retrieved');
  }

  async getActivityLog(
    userId: string,
    params: { page?: number; pageSize?: number; timeFilter?: string; actionFilter?: string; statusFilter?: string } = {}
  ) {
    const q = new URLSearchParams({ user_id: userId });
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('page_size', String(params.pageSize));
    if (params.timeFilter) q.set('time_filter', params.timeFilter);
    if (params.actionFilter) q.set('action_filter', params.actionFilter);
    if (params.statusFilter) q.set('status_filter', params.statusFilter);
    const raw = await api.get<any>(`/gmb/governance/activity-log?${q.toString()}`);
    return wrap(normalizeActivityLog(raw), 'Activity log retrieved');
  }

  async getAlerts(userId: string, severity: string = 'all') {
    const raw = await api.get<any>(`/gmb/governance/alerts?user_id=${userId}&severity=${severity}`);
    return wrap(normalizeAlerts(raw), 'Alerts retrieved');
  }

  async acknowledgeAlert(userId: string, alertId: string) {
    return api.request<any>(`/gmb/governance/alerts/${alertId}/acknowledge?user_id=${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async runAlertChecks(userId: string) {
    return api.post<any>(`/gmb/governance/run-alert-checks?user_id=${userId}`);
  }

  async getUnreadAlertCount(userId: string) {
    const raw = await api.get<any>(`/gmb/governance/alerts/unread-count?user_id=${userId}`);
    return raw?.data ?? raw;
  }

  async markAllAlertsRead(userId: string) {
    return api.request<any>(`/gmb/governance/alerts/mark-all-read?user_id=${userId}`, {
      method: 'PATCH',
    });
  }

  async downloadActivityLogCsv(userId: string): Promise<Blob> {
    return api.getBlob(`/gmb/governance/activity-log/export?user_id=${userId}`);
  }

  // â”€â”€ Phase 5 â€” Optimization â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  async getPerformanceTrends(
    userId: string,
    period: 'weekly' | 'monthly' = 'weekly'
  ) {
    return api.get<any>(
      `/gmb/optimization/performance-trends?user_id=${userId}&period=${period}`
    );
  }

  async getSearchQueries(userId: string) {
    return api.get<any>(`/gmb/optimization/search-queries?user_id=${userId}`);
  }

  async getReputationInsights(userId: string) {
    return api.get<any>(`/gmb/optimization/reputation-insights?user_id=${userId}`);
  }

  async getContentPerformance(userId: string) {
    return api.get<any>(`/gmb/optimization/content-performance?user_id=${userId}`);
  }

  async getEngagementInsights(userId: string) {
    return api.get<any>(`/gmb/optimization/engagement-insights?user_id=${userId}`);
  }

  async getRecommendations(userId: string) {
    return api.get<any>(`/gmb/optimization/recommendations?user_id=${userId}`);
  }

  async getMonthlyAudit(userId: string) {
    return api.get<any>(`/gmb/optimization/monthly-audit?user_id=${userId}`);
  }
}

export const gmbApi = new GmbApiService();
export default gmbApi;
